import { COMMUNITY_INITIATIVES, FUTURE_INITIATIVES, NEW_MUSLIM_FUTURE_CONTENT } from "@/data/content";
import { Link } from "react-router-dom";
import { ArrowUpRight, Sparkle } from "lucide-react";

export default function CommunitySection({ full = false }) {
  return (
    <section data-testid="community-section" className="py-20 md:py-28 bg-white">
      <div className="max-w-7xl mx-auto px-5 md:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <span className="overline">Beyond Travel</span>
            <h2 className="font-serif text-4xl md:text-5xl text-navy mt-3 leading-[1.05] tracking-tight">
              Community Initiatives
            </h2>
            <div className="mt-4 flex items-center gap-3">
              <span className="gold-divider" />
              <span className="text-sm text-[var(--maqam-muted)]">Faith. Community. Belonging.</span>
            </div>
            <p className="mt-6 text-[var(--maqam-muted)] leading-relaxed">
              Maqam is about more than the journey to the two holy cities. Alongside our Umrah
              trips, we're building community initiatives that grow over time — rooted in service,
              connection, and support for one another.
            </p>
            {!full && (
              <Link
                to="/community"
                data-testid="community-learn-more"
                className="mt-8 inline-flex items-center btn-primary rounded-full px-6 py-3 font-semibold"
              >
                Explore Initiatives
              </Link>
            )}
          </div>

          <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-5">
            {COMMUNITY_INITIATIVES.map((c, i) => (
              <article
                key={i}
                data-testid={`community-card-${i}`}
                className="card-elevated rounded-xl p-6 flex flex-col"
              >
                <div className="flex items-start justify-between gap-2">
                  <h3 className="font-serif text-xl text-navy leading-tight">{c.title}</h3>
                  <ArrowUpRight size={18} className="text-gold flex-shrink-0" />
                </div>
                {c.tag && (
                  <span
                    className="mt-3 inline-flex self-start items-center rounded-full px-3 py-1 text-[10px] uppercase tracking-[0.14em] font-semibold"
                    style={{
                      background: c.tag === "Active" ? "rgba(200,162,74,0.16)" : "var(--maqam-surface)",
                      color: c.tag === "Active" ? "var(--maqam-gold-text)" : "var(--maqam-muted)",
                      border: "1px solid var(--maqam-border)",
                    }}
                  >
                    {c.tag}
                  </span>
                )}
                <p className="mt-3 text-sm text-[var(--maqam-muted)] leading-relaxed">{c.body}</p>
              </article>
            ))}
          </div>
        </div>

        {/* Full page extras */}
        {full && (
          <>
            {/* New Muslim Umrah Program — future content placeholders */}
            <div
              data-testid="new-muslim-future"
              className="mt-16 rounded-2xl border border-[var(--maqam-border)] bg-[var(--maqam-surface)] p-7 md:p-10"
            >
              <div className="flex items-center gap-2">
                <span className="overline">New Muslim Umrah Program</span>
              </div>
              <h3 className="mt-3 font-serif text-2xl md:text-3xl text-navy leading-tight max-w-2xl">
                A growing initiative — with more to come.
              </h3>
              <p className="mt-4 text-[var(--maqam-muted)] leading-relaxed max-w-2xl">
                As this initiative grows, this space will feature the following. These are planned
                additions, not yet available.
              </p>
              <div className="mt-6 flex flex-wrap gap-2">
                {NEW_MUSLIM_FUTURE_CONTENT.map((item, i) => (
                  <span
                    key={i}
                    data-testid={`new-muslim-placeholder-${i}`}
                    className="inline-flex items-center gap-2 rounded-full border border-dashed border-[var(--maqam-border)] bg-white px-4 py-2 text-sm text-[var(--maqam-muted)]"
                  >
                    {item}
                    <span className="text-[10px] uppercase tracking-[0.14em] text-[color:var(--maqam-gold-text)]">Coming soon</span>
                  </span>
                ))}
              </div>
            </div>

            {/* Future Initiatives — concepts only */}
            <div data-testid="future-initiatives" className="mt-10">
              <span className="overline">Future Initiatives</span>
              <p className="mt-2 text-sm text-[var(--maqam-muted)] max-w-2xl">
                The following are concepts under consideration. They are not active services and
                have no registration, matching, or profiles at this time.
              </p>
              <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-5 max-w-3xl">
                {FUTURE_INITIATIVES.map((f, i) => (
                  <article
                    key={i}
                    data-testid={`future-initiative-${i}`}
                    className="rounded-xl border border-dashed border-[var(--maqam-border)] bg-white p-6"
                  >
                    <div className="flex items-center gap-2">
                      <Sparkle size={16} className="text-gold" />
                      <h4 className="font-serif text-lg text-navy">{f.title}</h4>
                      <span className="ml-auto text-[10px] uppercase tracking-[0.14em] text-[var(--maqam-muted)]">
                        Concept
                      </span>
                    </div>
                    <p className="mt-2 text-sm text-[var(--maqam-muted)]">{f.body}</p>
                  </article>
                ))}
              </div>
            </div>
          </>
        )}
      </div>
    </section>
  );
}
